import { useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * Width preset for the modal. `full` is "use most of the viewport" and is
   * intended for data-heavy modals (e.g. hunting result tables) where the
   * default Tailwind `max-w-*` values are too cramped.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  danger?: boolean;
}

/**
 * Reusable modal component with backdrop and keyboard handling.
 *
 * Rendered through a portal onto document.body. Rendering inline broke
 * whenever a Modal sat inside a `Card`: the card's `backdrop-blur-sm`
 * (a `backdrop-filter`) makes it the containing block for `position: fixed`
 * descendants, so `fixed inset-0` resolved to the card's box — the modal was
 * pinned to the card while the body scroll lock froze the page.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  danger = false,
}: ModalProps) {
  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Below `sm` the modal renders as a full-width bottom sheet; the size
  // presets only constrain width from `sm` up.
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    full: 'sm:max-w-[95vw]',
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={cn(
          'relative w-full mx-0 sm:mx-4 bg-bg-primary shadow-2xl',
          'rounded-t-2xl rounded-b-none sm:rounded-lg',
          'animate-sheet-up sm:animate-scale-in',
          'pb-[env(safe-area-inset-bottom)]',
          sizeClasses[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-default">
          <h2 className={cn(
            'text-lg font-semibold',
            danger ? 'text-red-600 dark:text-red-400' : 'text-text-primary'
          )}>
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-text-secondary">
              {description}
            </p>
          )}
        </div>
        
        {/* Content */}
        {children && (
          <div className="px-6 py-4 overflow-y-auto max-h-[70dvh] sm:max-h-[calc(100vh-12rem)]">
            {children}
          </div>
        )}
        
        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-border-default bg-bg-tertiary/50 rounded-b-none sm:rounded-b-lg">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// CONFIRM MODAL
// ============================================================================

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
}

/**
 * Confirmation modal for dangerous actions
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
}: ConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      danger={danger}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="text-text-secondary">
        {message}
      </p>
    </Modal>
  );
}

// ============================================================================
// DEFENDER ACTION MODAL
// ============================================================================

interface DefenderActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  children?: ReactNode;
  loading?: boolean;
}

/**
 * Modal specifically for Defender write-back actions
 * Includes warning about customer-facing changes
 */
export function DefenderActionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  children,
  loading = false,
}: DefenderActionModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      danger
      size="md"
      footer={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-medium">This will update the customer's Defender</span>
          </div>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Confirm Action'}
            </Button>
          </div>
        </div>
      }
    >
      {children}
    </Modal>
  );
}

